import React, { useState, useEffect, useCallback } from "react";
import "./B10CEDetailView.css";
import { fetchB10CEDetail, fetchTradeInfo, fetchB10CETriggerCounts, generateSnapshots, fetchAvailableTimes } from "../../services/exitSnapshotService";

/**
 * B10CE Detail View — 100% data-driven.
 * Renders whatever the server returns: groups, exits, conditions, metrics.
 * Adding/changing exit rules on the server = zero frontend changes.
 */

// ─── Group badge colors by code ─────────────────────────────────────────
const GROUP_COLORS = {
  GN: { bg: "#0d4d1f", color: "#3fb950" },
  SW: { bg: "#1a2744", color: "#58a6ff" },
  GD: { bg: "#4d0d0d", color: "#f85149" },
  GU: { bg: "#3d2e00", color: "#d29922" },
};

function pctClass(pct) {
  if (pct >= 100) return "high";
  if (pct >= 50) return "mid";
  return "low";
}

// ─── Sub-components ─────────────────────────────────────────────────────

function ConditionRow({ cond }) {
  // OR condition — show each alternative group
  if (cond.type === "OR" && cond.orGroups) {
    return (
      <div className="b10-or-cond">
        <div className="b10-or-label">
          <span className={`b10-cond-dot b10-cond-dot--${cond.passed ? "pass" : "fail"}`} />
          <span className="b10-or-text">OR condition:</span>
        </div>
        <div className="b10-or-groups">
          {cond.orGroups.map((group, gi) => (
            <div key={gi} className={`b10-or-group ${group.groupPassed ? "b10-or-group--pass" : ""}`}>
              {gi > 0 && <span className="b10-or-divider">OR</span>}
              {(group.conditions || []).map((c, ci) => (
                <div key={ci} className="b10-cond-row">
                  <span className={`b10-cond-dot b10-cond-dot--${c.passed ? "pass" : "fail"}`} />
                  <span className="b10-cond-expr">{c.label}</span>
                  <span className={`b10-cond-val b10-cond-val--${c.passed ? "pass" : "fail"}`}>
                    {c.actualValue === null || c.actualValue === undefined ? "N/A" : String(c.actualValue)}
                  </span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Simple condition
  return (
    <div className="b10-cond-row">
      <span className={`b10-cond-dot b10-cond-dot--${cond.passed ? "pass" : "fail"}`} />
      <span className="b10-cond-expr">{cond.label}</span>
      <span className={`b10-cond-val b10-cond-val--${cond.passed ? "pass" : "fail"}`}>
        {cond.actualValue === null || cond.actualValue === undefined
          ? "N/A"
          : String(cond.actualValue)}
      </span>
    </div>
  );
}

function ExitRow({ exit, triggerInfo, onDateClick }) {
  const [open, setOpen] = useState(false);
  const [showDates, setShowDates] = useState(false);
  const cls = pctClass(exit.matchPct);
  const count = triggerInfo?.count || 0;
  const dates = triggerInfo?.dates || [];

  return (
    <>
      <div
        className={`b10-exit-row ${exit.matched ? "b10-exit-row--matched" : ""}`}
        onClick={() => setOpen(!open)}
      >
        <span className={`b10-exit-status b10-exit-status--${exit.matched ? "pass" : "fail"}`}>
          {exit.matched ? "\u2713" : "\u2717"}
        </span>
        <span className={`b10-exit-code b10-exit-code--${exit.matched ? "pass" : "fail"}`}>
          {exit.exitCode}
        </span>
        <span className="b10-exit-family">{exit.family}</span>
        {exit.rating && (
          <span className={`b10-exit-rating b10-exit-rating--${exit.rating}`}>{exit.rating}</span>
        )}
        <span className="b10-exit-bar-wrap">
          <span className="b10-exit-bar-bg">
            <span
              className={`b10-exit-bar-fill b10-exit-bar-fill--${cls}`}
              style={{ width: `${exit.matchPct}%` }}
            />
          </span>
        </span>
        <span className={`b10-exit-pct b10-exit-pct--${cls}`}>
          {exit.passedCount}/{exit.totalCount}
        </span>
        <span className={`b10-exit-pct-num b10-exit-pct-num--${cls}`}>{exit.matchPct}%</span>
        {/* Trigger count badge — click to show dates */}
        <span
          className={`b10-trigger-count ${count > 0 ? "b10-trigger-count--has" : ""}`}
          onClick={(e) => { e.stopPropagation(); if (count > 0) setShowDates(!showDates); }}
          title={count > 0 ? `Triggered ${count} times. Click to see dates.` : "Never triggered"}
        >
          {count}
        </span>
        <span className="b10-exit-arrow">{open ? "\u25B2" : "\u25BC"}</span>
      </div>
      {/* Trigger dates dropdown */}
      {showDates && dates.length > 0 && (
        <div className="b10-trigger-dates">
          <span className="b10-trigger-dates__label">Triggered on {dates.length} dates:</span>
          <div className="b10-trigger-dates__list">
            {dates.map((d) => {
              const dateStr = typeof d === "string" ? d : d.date;
              const opening = typeof d === "string" ? "" : d.opening;
              const entry = typeof d === "string" ? "" : (d.entry || "");
              const info = [opening, entry].filter(Boolean).join(", ");
              return (
                <span
                  key={dateStr}
                  className="b10-trigger-date-chip"
                  onClick={() => onDateClick && onDateClick(dateStr)}
                  title={`Jump to ${dateStr}`}
                >
                  {dateStr}{info ? ` (${info})` : ""}
                </span>
              );
            })}
          </div>
        </div>
      )}
      {/* Conditions dropdown */}
      {open && (
        <div className="b10-exit-detail">
          <div className="b10-cond-grid">
            {(exit.conditions || []).map((c, i) => (
              <ConditionRow key={i} cond={c} />
            ))}
          </div>
        </div>
      )}
    </>
  );
}

function GroupSection({ group, triggerCounts, onDateClick }) {
  const gc = GROUP_COLORS[group.groupCode] || { bg: "#21262d", color: "#8b949e" };
  const dimmed = !group.applicable;

  return (
    <div style={dimmed ? { opacity: 0.45 } : {}}>
      <div className="b10-group-hdr">
        <span className="b10-group-badge" style={{ background: gc.bg, color: gc.color }}>
          {group.groupCode}
        </span>
        <span className="b10-group-label">{group.groupLabel}</span>
        {dimmed && <span style={{ fontSize: 10, color: "#484f58", marginLeft: 4 }}>(not active today)</span>}
        <span className="b10-group-count">
          {group.totalExits} exits &middot; {group.matchedExits} matched
        </span>
      </div>
      {(group.exits || []).map((ex) => (
        <ExitRow
          key={ex.exitCode}
          exit={ex}
          triggerInfo={triggerCounts?.[ex.exitCode]}
          onDateClick={onDateClick}
        />
      ))}
    </div>
  );
}

function MetricRow({ name, value }) {
  const isTrue = value === true;
  const isFalse = value === false;
  const isNum = typeof value === "number";
  let cls = "b10-mv--str";
  if (isTrue) cls = "b10-mv--true";
  else if (isFalse) cls = "b10-mv--false";
  else if (isNum) cls = "b10-mv--num";

  const text =
    value === null || value === undefined
      ? "\u2014"
      : isTrue
      ? "TRUE"
      : isFalse
      ? "FALSE"
      : isNum
      ? Number.isInteger(value)
        ? String(value)
        : value.toFixed(2)
      : String(value);

  return (
    <div className={`b10-mrow${isTrue ? " b10-mrow--true" : ""}`}>
      <span className="b10-mk" title={name}>{name}</span>
      <span className={`b10-mv ${cls}`}>{text}</span>
    </div>
  );
}

function MetricGroup({ name, fields }) {
  return (
    <div className="b10-mgroup">
      <div className="b10-mgroup-hdr">{name}</div>
      {Object.entries(fields).map(([k, v]) => (
        <MetricRow key={k} name={k} value={v} />
      ))}
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────

export default function B10CEDetailView({ indexName, optionType, onlyDate, onlyTime, onTimeChange, onDateChange, selectedClass, isExpiry }) {
  const className = selectedClass || "B10CE";
  const [data, setData] = useState(null);
  const [tradeInfo, setTradeInfo] = useState(null);
  const [triggerCounts, setTriggerCounts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [genStatus, setGenStatus] = useState(null);
  const [error, setError] = useState(null);

  // Load trade info once per date
  useEffect(() => {
    if (!indexName || !optionType || !onlyDate) return;
    fetchTradeInfo({ indexName, optionType, onlyDate, isExpiry })
      .then(setTradeInfo)
      .catch(() => setTradeInfo(null));
  }, [indexName, optionType, onlyDate, isExpiry]);

  // Load trigger counts once per index/optionType (cached, doesn't change per date)
  useEffect(() => {
    if (!indexName || !optionType) return;
    fetchB10CETriggerCounts({ indexName, optionType, className, isExpiry })
      .then(setTriggerCounts)
      .catch(() => setTriggerCounts(null));
  }, [indexName, optionType, className, isExpiry]);

  // Load exit class detail when time changes
  const loadDetail = useCallback(() => {
    if (!indexName || !optionType || !onlyDate || !onlyTime) return;
    setLoading(true);
    setError(null);
    fetchB10CEDetail({ indexName, optionType, onlyDate, onlyTime, className, isExpiry })
      .then((d) => {
        if (d?.error) {
          setError(d.error);
          setData(null);
        } else {
          setData(d);
          setError(null);
        }
      })
      .catch((e) => {
        setError(e?.message || "Failed to load");
        setData(null);
      })
      .finally(() => setLoading(false));
  }, [indexName, optionType, onlyDate, onlyTime, className, isExpiry]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const [groupFilter, setGroupFilter] = useState("ALL");
  const [sortBy, setSortBy] = useState("matchPct");
  const [sortDir, setSortDir] = useState("desc");

  const RATING_ORDER = { A: 1, B: 2, C: 3, D: 4 };
  const handleSort = (field) => {
    if (sortBy === field) { setSortDir(d => d === "desc" ? "asc" : "desc"); }
    else { setSortBy(field); setSortDir(field === "rating" ? "asc" : "desc"); }
  };
  const sortExits = (exits) => {
    return [...exits].sort((a, b) => {
      let cmp = 0;
      if (sortBy === "matchPct") { cmp = a.matchPct - b.matchPct; }
      else if (sortBy === "rating") { cmp = (RATING_ORDER[a.rating] || 5) - (RATING_ORDER[b.rating] || 5); }
      else if (sortBy === "triggers") { cmp = (triggerCounts?.[a.exitCode]?.count || 0) - (triggerCounts?.[b.exitCode]?.count || 0); }
      return sortDir === "desc" ? -cmp : cmp;
    });
  };

  const groups = data?.groups || [];
  const baseGroups = groupFilter === "ALL"
    ? groups
    : groups.filter((g) => g.groupCode === groupFilter || g.groupCode === "GN"
        || g.groupCode.startsWith(groupFilter + "_"));
  const filteredGroups = baseGroups.map(g => ({ ...g, exits: sortExits(g.exits || []) }));
  const grouped = data?.exitRuleDataGrouped || {};
  const summary = data?.summary || {};

  return (
    <div className="b10-root">
      {/* ── Trade Timeline ─────────────────────────────────── */}
      {tradeInfo && (
        <div className="b10-timeline">
          <div className="b10-tl-label">
            {tradeInfo.entryTime && (
              <span className="b10-tl-entry">{"\u25B6"} Entry {tradeInfo.entryTime}</span>
            )}
            {tradeInfo.entryTime && <span className="b10-tl-arrow">{"\u2192"}</span>}
            <span className="b10-tl-current">{"\u25CF"} Current {onlyTime}</span>
            {tradeInfo.exitTime && <span className="b10-tl-arrow">{"\u2192"}</span>}
            {tradeInfo.exitTime && (
              <span className="b10-tl-exit">{"\u25A0"} Exit {tradeInfo.exitTime}</span>
            )}
            {tradeInfo.openingType && (
              <span className="b10-tl-opening">{tradeInfo.openingType}</span>
            )}
            {tradeInfo.availableTimes && tradeInfo.availableTimes.length > 0 && (
              <span className="b10-tl-times">
                Available:{" "}
                {tradeInfo.availableTimes.map((t) => (
                  <span
                    key={t}
                    className={`b10-tl-time-chip${t.startsWith(onlyTime) ? " b10-tl-time-chip--active" : ""}`}
                    onClick={() => onTimeChange && onTimeChange(t.slice(0, 5))}
                  >
                    {t.slice(0, 5)}
                  </span>
                ))}
              </span>
            )}
          </div>
        </div>
      )}

      {/* ── Header ─────────────────────────────────────────── */}
      <div className="b10-detail-hdr">
        <span className="b10-detail-title">{className}</span>
        <span className="b10-detail-stat">
          Time: <span className="b10-detail-val" style={{ color: "#3fb950" }}>{onlyTime}</span>
        </span>
        {summary.totalApplicableExits !== undefined && (
          <span className="b10-detail-stat">
            Exits: <span className="b10-detail-val">
              {summary.matchedExits}/{summary.totalApplicableExits} matched
            </span>
          </span>
        )}
        {summary.overallMatchPct !== undefined && (
          <span className="b10-detail-stat">
            Conditions: <span className="b10-detail-val">
              {summary.passedConditions}/{summary.totalConditions} ({summary.overallMatchPct}%)
            </span>
          </span>
        )}
        {data?.openingType && (
          <span className="b10-detail-stat">
            Opening: <span className="b10-detail-val">{data.openingType}</span>
          </span>
        )}
        {data?.source && (
          <span className={`b10-source-badge b10-source-badge--${data.source}`}>
            from {data.source}
          </span>
        )}
        {/* Regenerate snapshots button */}
        <button
          className="b10-regen-btn"
          disabled={generating}
          onClick={() => {
            setGenerating(true);
            setGenStatus("Generating...");
            generateSnapshots({ indexName, onlyDate })
              .then((r) => {
                setGenStatus(`Done! ${r.timeCount} minutes generated`);
                // Reload available times and current detail
                fetchAvailableTimes({ indexName, optionType, onlyDate, isExpiry })
                  .then((t) => {
                    const times = t?.times || [];
                    if (onTimeChange && times.length > 0) {
                      onTimeChange(onlyTime);
                    }
                  });
                loadDetail();
                setTimeout(() => setGenStatus(null), 4000);
              })
              .catch(() => setGenStatus("Failed"))
              .finally(() => setGenerating(false));
          }}
        >
          {generating ? "\u23F3 Generating..." : "\u21BB Regenerate Snapshots"}
        </button>
        {genStatus && !generating && (
          <span style={{ fontSize: 10, color: "#3fb950" }}>{genStatus}</span>
        )}
      </div>

      {/* ── Loading / Error ────────────────────────────────── */}
      {loading && (
        <div className="b10-loading">
          <div className="b10-spinner" />
          Loading {className} conditions...
        </div>
      )}

      {!loading && error && (
        <div className="b10-empty">
          <div style={{ color: "#f85149" }}>{error}</div>
          {tradeInfo?.availableTimes?.length > 0 && (
            <div style={{ color: "#8b949e", fontSize: 11 }}>
              Available times: {tradeInfo.availableTimes.map((t) => t.slice(0, 5)).join(", ")}
            </div>
          )}
        </div>
      )}

      {/* ── Exit Groups ────────────────────────────────────── */}
      {!loading && !error && data && (
        <div className="b10-scroll">
          {/* Opening type filter */}
          <div className="b10-group-filter">
            {["ALL", "GN", "GU", "SW", "GD"].map((f) => (
              <span
                key={f}
                className={`b10-group-filter__btn${groupFilter === f ? " b10-group-filter__btn--active" : ""}`}
                onClick={() => setGroupFilter(f)}
              >
                {f === "ALL" ? "ALL" : f === "GN" ? "GENERAL" : f === "GU" ? "GAP UP" : f === "SW" ? "SIDEWAYS" : "GAP DOWN"}
              </span>
            ))}
            {data?.openingType && (
              <span className="b10-group-filter__info">
                Today: {data.openingType}
              </span>
            )}
            {/* Sort controls */}
            <div className="b10-sort-controls">
              <span className="b10-sort-label">Sort:</span>
              {[
                { key: "matchPct", label: "Match%" },
                { key: "rating", label: "Rating" },
                { key: "triggers", label: "Triggers" },
              ].map(({ key, label }) => (
                <span
                  key={key}
                  className={`b10-sort-btn${sortBy === key ? " b10-sort-btn--active" : ""}`}
                  onClick={() => handleSort(key)}
                >
                  {label}
                  <span className="b10-sort-arrow">{sortBy === key ? (sortDir === "desc" ? "\u2193" : "\u2191") : ""}</span>
                </span>
              ))}
            </div>
          </div>

          {/* Exit rows grouped */}
          {filteredGroups.map((g) => (
            <GroupSection
              key={g.groupCode}
              group={g}
              triggerCounts={triggerCounts}
              onDateClick={(date) => onDateChange && onDateChange(date)}
            />
          ))}

          {/* ── All Data Points (metrics) ────────────────── */}
          {Object.keys(grouped).length > 0 && (
            <>
              <div className="b10-metrics-hdr">
                <span className="b10-metrics-title">ALL DATA POINTS AT {onlyTime}</span>
                <span className="b10-metrics-sub">
                  Raw exitRuleData values used to evaluate conditions above
                </span>
              </div>
              <div className="b10-metrics-scroll">
                <div className="b10-metrics-row">
                  {Object.entries(grouped).map(([name, fields]) => (
                    <MetricGroup key={name} name={name} fields={fields} />
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
